import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../../helpers/axiosInstance";
import { toast } from "react-toastify";
import { ReportCatalogModules, ReportCatalogModulesConstant, ReportJobsStatusConstant } from "../../../pages/report-catalog/report-catalog.constants";

// Utility function to generate unique job IDs
const generateJobId = (jobs) => {
  const lastNum = Math.max(
    0,
    ...jobs.map((j) => {
      const num = parseInt(j.id?.split("-")[1]);
      return isNaN(num) ? 0 : num;
    })
  );
  return `job-${(lastNum + 1).toString().padStart(3, "0")}`;
};

const handleJobPending = (jobs, module) => {
    const id = generateJobId(jobs);
    const roleModule = ReportCatalogModules().find(m => m.value === ReportCatalogModulesConstant?.[module]);
    return {
        id,
        moduleName: roleModule?.label,
        status: ReportJobsStatusConstant?.progress,
        submitDateTime: Date.now(), // store timestamp
        duration: null,
    }
};

const handleJobComplete = (jobs, module) => {
    const roleModule = ReportCatalogModules().find(m => m.value === ReportCatalogModulesConstant?.[module]);
    return jobs.map((job) =>
        job.status === ReportJobsStatusConstant?.progress && job.moduleName === roleModule?.label
            ? {
                ...job,
                status: ReportJobsStatusConstant?.complete,
                duration: Date.now() - job.submitDateTime, // ms difference
            }
            : job
    );
};

const handleJobFail = (jobs, module) => {
    const roleModule = ReportCatalogModules().find(m => m.value === ReportCatalogModulesConstant?.[module]);
    return jobs.map((job) =>
        job.status === ReportJobsStatusConstant?.progress && job.moduleName === roleModule?.label
            ? { ...job, status: ReportJobsStatusConstant?.failed, duration: Date.now() - job.submitDateTime }
            : job
    );
};

const initialState = {
    exportData: {
        role: { loading: false, data: [], history: [], error: null },
        user: { loading: false, data: [], history: [], error: null },
        participant: { loading: false, data: [], history: [], error: null },
        ruleEngine: { loading: false, data: [], history: [], error: null },
        mcc: { loading: false, data: [], history: [], error: null },
        countryCodeBlock: { loading: false, data: [], history: [], error: null },
        authentication: { loading: false, data: [], history: [], error: null },
    },
    jobs: [], // { id: '', moduleName: '', status: '', submitDateTime: '', duration: '' }
    specificLoading: null,
};

// ------------------------------- OPEN ROLE MANAGEMENT -------------------------------

export const getAllRoleExportHistoryData = createAsyncThunk(
    "report-catalog/get-all-role-export-history-data",
    async ({ user }) => {
        try {
            const { url, endpoint } = user?.routesConfig;

            const base = url?.backoffice;
            const endpointpath = endpoint?.backoffice?.rolesexporthistory;
            const institution = user?.institution?.id;

            const apiURL = base + endpointpath + '/' + institution;

            const response = await axiosInstance.get(apiURL);

            // if(response?.data?.responseCode === '404') {
            //     toast.error(response?.data?.responseDescription);
            // }

            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

export const getAllRoleExportModuleData = createAsyncThunk(
    "report-catalog/get-all-role-export-data",
    async ({ user, params }) => {
        try {
            const { url, endpoint } = user?.routesConfig;

            const base = url?.backoffice;
            const endpointpath = endpoint?.backoffice?.rolesreport;

            const apiURL = base + endpointpath;

            const response = await axiosInstance.post(apiURL, params);

            // if(response?.data?.responseCode === '404') {
            //     toast.error(response?.data?.responseDescription);
            // }

            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

export const getSpecificRoleHistoryData = createAsyncThunk(
    "report-catalog/get-specific-role-history-data",
    async ({ user, id }) => {
        try {
            const { url, endpoint } = user?.routesConfig;

            const base = url?.backoffice;
            const endpointpath = endpoint?.backoffice?.rolesexportsdownload;

            const apiURL = base + endpointpath + '/' + id;

            const response = await axiosInstance.get(apiURL);

            // if(response?.data?.responseCode === '404') {
            //     toast.error(response?.data?.responseDescription);
            // }

            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

// ------------------------------- CLOSE ROLE MANAGEMENT -------------------------------

// ------------------------------- OPEN USER MANAGEMENT -------------------------------

export const getAllUserExportHistoryData = createAsyncThunk(
    "report-catalog/get-all-user-export-history-data",
    async ({ user }) => {
        try {
            const { url, endpoint } = user?.routesConfig;

            const base = url?.backoffice;
            const endpointpath = endpoint?.backoffice?.usersexporthistory;
            const institution = user?.institution?.id;

            const apiURL = base + endpointpath + '/' + institution;

            const response = await axiosInstance.get(apiURL);

            // if(response?.data?.responseCode === '404') {
            //     toast.error(response?.data?.responseDescription);
            // }

            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

export const getAllUserExportModuleData = createAsyncThunk(
    "report-catalog/get-all-user-export-data",
    async ({ user, params }) => {
        try {
            const { url, endpoint } = user?.routesConfig;

            const base = url?.backoffice;
            const endpointpath = endpoint?.backoffice?.usersreport;

            const apiURL = base + endpointpath;

            const response = await axiosInstance.post(apiURL, params);

            // if(response?.data?.responseCode === '404') {
            //     toast.error(response?.data?.responseDescription);
            // }

            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

export const getSpecificUserHistoryData = createAsyncThunk(
    "report-catalog/get-specific-user-history-data",
    async ({ user, id }) => {
        try {
            const { url, endpoint } = user?.routesConfig;

            const base = url?.backoffice;
            const endpointpath = endpoint?.backoffice?.usersexportsdownload;

            const apiURL = base + endpointpath + '/' + id;

            const response = await axiosInstance.get(apiURL);

            // if(response?.data?.responseCode === '404') {
            //     toast.error(response?.data?.responseDescription);
            // }

            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

// ------------------------------- CLOSE USER MANAGEMENT -------------------------------

// ------------------------------- OPEN PARTICIPANT MANAGEMENT -------------------------------

export const getAllParticipantExportHistoryData = createAsyncThunk(
    "report-catalog/get-all-participant-export-history-data",
    async ({ user }) => {
        try {
            const { url, endpoint } = user?.routesConfig;
            const base = url?.acsbackofficemanagement;
            const endpointpath = endpoint?.acsbackofficemanagement?.institutionsexporthistory;
            const institution = user?.institution?.id;
            // institutions/exports/history
            const apiURL = base + endpointpath + '/' + institution;
            const response = await axiosInstance.get(apiURL);
            // if(response?.data?.responseCode === '404') {
            //     toast.error(response?.data?.responseDescription);
            // }
            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

export const getAllParticipantExportModuleData = createAsyncThunk(
    "report-catalog/get-all-participant-export-data",
    async ({ user, params }) => {
        try {
            const { url, endpoint } = user?.routesConfig;
            const base = url?.acsbackofficemanagement;
            const endpointpath = endpoint?.acsbackofficemanagement?.institutionsreport;
            const apiURL = base + endpointpath;
            const response = await axiosInstance.post(apiURL, params);
            // if(response?.data?.responseCode === '404') {
            //     toast.error(response?.data?.responseDescription);
            // }
            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

export const getSpecificParticipantHistoryData = createAsyncThunk(
    "report-catalog/get-specific-participant-history-data",
    async ({ user, id }) => {
        try {
            const { url, endpoint } = user?.routesConfig;
            const base = url?.acsbackofficemanagement;
            const endpointpath = endpoint?.acsbackofficemanagement?.institutionsexportsdownload;
            const apiURL = base + endpointpath + '/' + id;
            const response = await axiosInstance.get(apiURL);
            // if(response?.data?.responseCode === '404') {
            //     toast.error(response?.data?.responseDescription);
            // }
            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

// ------------------------------- CLOSE PARTICIPANT MANAGEMENT -------------------------------

// ------------------------------- OPEN RULE ENGINE MANAGEMENT -------------------------------

export const getAllRuleEngineExportHistoryData = createAsyncThunk(
    "report-catalog/get-all-rule-engine-export-history-data",
    async ({ user }) => {
        try {
            const { url, endpoint } = user?.routesConfig;

            const base = url?.acsbackofficemanagement;
            const endpointpath = endpoint?.acsbackofficemanagement?.ruleengineexporthistory;
            const institution = user?.institution?.id;

            const apiURL = base + endpointpath + '/' + institution;

            const response = await axiosInstance.get(apiURL);

            // if(response?.data?.responseCode === '404') {
            //     toast.error(response?.data?.responseDescription);
            // }

            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

export const getAllRuleEngineExportModuleData = createAsyncThunk(
    "report-catalog/get-all-rule-engine-export-module-data",
    async ({ user, params }) => {
        try {
            const { url, endpoint } = user?.routesConfig;

            const base = url?.acsbackofficemanagement;
            const endpointpath = endpoint?.acsbackofficemanagement?.ruleenginereport;

            const apiURL = base + endpointpath;

            const response = await axiosInstance.post(apiURL, params);

            // if(response?.data?.responseCode === '404') {
            //     toast.error(response?.data?.responseDescription);
            // }

            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

export const getSpecificRuleEngineHistoryData = createAsyncThunk(
    "report-catalog/get-specific-rule-engine-history-data",
    async ({ user, id }) => {
        try {
            const { url, endpoint } = user?.routesConfig;

            const base = url?.acsbackofficemanagement;
            const endpointpath = endpoint?.acsbackofficemanagement?.ruleengineexportsdownload;

            const apiURL = base + endpointpath + '/' + id;

            const response = await axiosInstance.get(apiURL);

            // if(response?.data?.responseCode === '404') {
            //     toast.error(response?.data?.responseDescription);
            // }

            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

// ------------------------------- CLOSE RULE ENGINE MANAGEMENT -------------------------------

// ------------------------------- OPEN MCC BLOCK MANAGEMENT -------------------------------

export const getAllMccExportHistoryData = createAsyncThunk(
    "report-catalog/get-all-mcc-export-history-data",
    async ({ user }) => {
        try {
            const { url, endpoint } = user?.routesConfig;
            const base = url?.acsbackofficemanagement;
            const endpointpath = endpoint?.acsbackofficemanagement?.mccexporthistory;
            const institution = user?.institution?.id;
            const apiURL = base + endpointpath + '/' + institution;
            const response = await axiosInstance.get(apiURL);
            // if(response?.data?.responseCode === '404') {
            //     toast.error(response?.data?.responseDescription);
            // }
            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);
export const getAllMccExportModuleData = createAsyncThunk(
    "report-catalog/get-all-mcc-export-data",
    async ({ user, params }) => {
        try {
            const { url, endpoint } = user?.routesConfig;
            const base = url?.acsbackofficemanagement;
            const endpointpath = endpoint?.acsbackofficemanagement?.mccreport;
            const apiURL = base + endpointpath;
            const response = await axiosInstance.post(apiURL, params);
            // if(response?.data?.responseCode === '404') {
            //     toast.error(response?.data?.responseDescription);
            // }
            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);
export const getSpecificMccHistoryData = createAsyncThunk(
    "report-catalog/get-specific-mcc-history-data",
    async ({ user, id }) => {
        try {
            const { url, endpoint } = user?.routesConfig;
            const base = url?.acsbackofficemanagement;
            const endpointpath = endpoint?.acsbackofficemanagement?.mccexportsdownload;
            const apiURL = base + endpointpath + '/' + id;
            const response = await axiosInstance.get(apiURL);
            // if(response?.data?.responseCode === '404') {
            //     toast.error(response?.data?.responseDescription);
            // }
            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

// ------------------------------- CLOSE MCC BLOCK MANAGEMENT -------------------------------

// ------------------------------- OPEN COUNTRY CODE BLOCK MANAGEMENT -------------------------------

export const getAllCountryCodeBlockExportHistoryData = createAsyncThunk(
    "report-catalog/get-all-country-code-block-export-history-data",
    async ({ user }) => {
        try {
            const { url, endpoint } = user?.routesConfig;

            const base = url?.acsbackofficemanagement;
            const endpointpath = endpoint?.acsbackofficemanagement?.countryblockingexporthistory;
            const institution = user?.institution?.id;
            // institutions/exports/history

            const apiURL = base + endpointpath + '/' + institution;

            const response = await axiosInstance.get(apiURL);

            // if(response?.data?.responseCode === '404') {
            //     toast.error(response?.data?.responseDescription);
            // }

            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

export const getAllCountryCodeBlockExportModuleData = createAsyncThunk(
    "report-catalog/get-all-country-code-block-export-data",
    async ({ user, params }) => {
        try {
            const { url, endpoint } = user?.routesConfig;

            const base = url?.acsbackofficemanagement;
            const endpointpath = endpoint?.acsbackofficemanagement?.countryblockingreport;
            const apiURL = base + endpointpath ;

            const response = await axiosInstance.post(apiURL, params);

            // if(response?.data?.responseCode === '404') {
            //     toast.error(response?.data?.responseDescription);
            // }

            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

export const getSpecificCountryCodeBlockHistoryData = createAsyncThunk(
    "report-catalog/get-specific-country-code-block-history-data",
    async ({ user, id }) => {
        try {
            const { url, endpoint } = user?.routesConfig;

            const base = url?.acsbackofficemanagement;
            const endpointpath = endpoint?.acsbackofficemanagement?.countryblockingexportsdownload;

            const apiURL = base + endpointpath + '/' + id;

            const response = await axiosInstance.get(apiURL);

            // if(response?.data?.responseCode === '404') {
            //     toast.error(response?.data?.responseDescription);
            // }

            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

// ------------------------------- CLOSE PARTICIPANT MANAGEMENT -------------------------------

// ------------------------------- OPEN AUTHENTICATION MANAGEMENT -------------------------------

export const getAllAuthenticationExportHistoryData = createAsyncThunk(
    "report-catalog/get-all-authentication-export-history-data",
    async ({ user }) => {
        try {
            const { url, endpoint } = user?.routesConfig;

            const base = url?.acsbackofficemanagement;
            const endpointpath = endpoint?.acsbackofficemanagement?.authenticationhistoryexportshistory;
            const institution = user?.institution?.id;
            // institutions/exports/history

            const apiURL = base + endpointpath + '/' + institution;

            const response = await axiosInstance.get(apiURL);

            // if(response?.data?.responseCode === '404') {
            //     toast.error(response?.data?.responseDescription);
            // }

            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

export const getAllAuthenticationExportModuleData = createAsyncThunk(
    "report-catalog/get-all-authentication-export-module-data",
    async ({ user, params }) => {
        try {
            const { url, endpoint } = user?.routesConfig;

            const base = url?.acsbackofficemanagement;
            const endpointpath = endpoint?.acsbackofficemanagement?.authenticationhistory;
            const apiURL = base + endpointpath ;

            const response = await axiosInstance.post(apiURL, params);

            // if(response?.data?.responseCode === '404') {
            //     toast.error(response?.data?.responseDescription);
            // }

            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

export const getSpecificAuthenticationHistoryData = createAsyncThunk(
    "report-catalog/get-specific-authentication-history-data",
    async ({ user, id }) => {
        try {
            const { url, endpoint } = user?.routesConfig;

            const base = url?.acsbackofficemanagement;
            const endpointpath = endpoint?.acsbackofficemanagement?.authenticationhistorydownload;

            const apiURL = base + endpointpath + '/' + id;

            const response = await axiosInstance.get(apiURL);

            // if(response?.data?.responseCode === '404') {
            //     toast.error(response?.data?.responseDescription);
            // }

            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

// ------------------------------- CLOSE PARTICIPANT MANAGEMENT -------------------------------

const reportCatalogSlice = createSlice({
    name: "report-catalog",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // GET ALL ROLE EXPORT MODULE DATA
            .addCase(getAllRoleExportModuleData.pending, (state) => {
                state.exportData.role.loading = true;
                state.exportData.role.error = null;
                state.jobs.push(handleJobPending(state.jobs, 'role'));
            })
            .addCase(getAllRoleExportModuleData.fulfilled, (state, action) => {
                state.exportData.role.loading = false;
                state.exportData.role.data = action.payload || [];
                state.jobs = handleJobComplete(state.jobs, 'role');
            })
            .addCase(getAllRoleExportModuleData.rejected, (state, action) => {
                state.exportData.role.loading = false;
                state.exportData.role.error = action.error.message;
                state.exportData.role.data = [];
                state.jobs = handleJobFail(state.jobs, 'role');
            })
            // GET ALL ROLE EXPORT HISTORY DATA
            .addCase(getAllRoleExportHistoryData.fulfilled, (state, action) => {
                state.exportData.role.history = action.payload || [];
            })


            // GET ALL USER EXPORT MODULE DATA
            .addCase(getAllUserExportModuleData.pending, (state) => {
                state.exportData.user.loading = true;
                state.exportData.user.error = null;
                state.jobs.push(handleJobPending(state.jobs, 'user'));
            })
            .addCase(getAllUserExportModuleData.fulfilled, (state, action) => {
                state.exportData.user.loading = false;
                state.exportData.user.data = action.payload || [];
                state.jobs = handleJobComplete(state.jobs, 'user');
            })
            .addCase(getAllUserExportModuleData.rejected, (state, action) => {
                state.exportData.user.loading = false;
                state.exportData.user.error = action.error.message;
                state.exportData.user.data = [];
                state.jobs = handleJobFail(state.jobs, 'user');
            })
            // GET ALL USER EXPORT HISTORY DATA
            .addCase(getAllUserExportHistoryData.fulfilled, (state, action) => {
                state.exportData.user.history = action.payload || [];
            })


            // GET ALL Participant EXPORT MODULE DATA
            .addCase(getAllParticipantExportModuleData.pending, (state) => {
                state.exportData.participant.loading = true;
                state.exportData.participant.error = null;
                state.jobs.push(handleJobPending(state.jobs, 'participant'));
            })
            .addCase(getAllParticipantExportModuleData.fulfilled, (state, action) => {
                state.exportData.participant.loading = false;
                state.exportData.participant.data = action.payload || [];
                state.jobs = handleJobComplete(state.jobs, 'participant');
            })
            .addCase(getAllParticipantExportModuleData.rejected, (state, action) => {
                state.exportData.participant.loading = false;
                state.exportData.participant.error = action.error.message;
                state.exportData.participant.data = [];
                state.jobs = handleJobFail(state.jobs, 'participant');
            })
            // GET ALL Participant EXPORT HISTORY DATA
            .addCase(getAllParticipantExportHistoryData.fulfilled, (state, action) => {
                state.exportData.participant.history = action.payload || [];
            })


            // GET ALL RULE ENGINE EXPORT MODULE DATA
            .addCase(getAllRuleEngineExportModuleData.pending, (state) => {
                state.exportData.ruleEngine.loading = true;
                state.exportData.ruleEngine.error = null;
                state.jobs.push(handleJobPending(state.jobs, 'ruleEngine'));
            })
            .addCase(getAllRuleEngineExportModuleData.fulfilled, (state, action) => {
                state.exportData.ruleEngine.loading = false;
                state.exportData.ruleEngine.data = action.payload || [];
                state.jobs = handleJobComplete(state.jobs, 'ruleEngine');
            })
            .addCase(getAllRuleEngineExportModuleData.rejected, (state, action) => {
                state.exportData.ruleEngine.loading = false;
                state.exportData.ruleEngine.error = action.error.message;
                state.exportData.ruleEngine.data = [];
                state.jobs = handleJobFail(state.jobs, 'ruleEngine');
            })
            // GET ALL RULE ENGINE EXPORT HISTORY DATA
            .addCase(getAllRuleEngineExportHistoryData.fulfilled, (state, action) => {
                state.exportData.ruleEngine.history = action.payload || [];
            })


            // GET ALL MCC EXPORT MODULE DATA
            .addCase(getAllMccExportModuleData.pending, (state) => {
                state.exportData.mcc.loading = true;
                state.exportData.mcc.error = null;
                state.jobs.push(handleJobPending(state.jobs, 'mcc'));
            })
            .addCase(getAllMccExportModuleData.fulfilled, (state, action) => {
                state.exportData.mcc.loading = false;
                state.exportData.mcc.data = action.payload || [];
                state.jobs = handleJobComplete(state.jobs, 'mcc');
            })
            .addCase(getAllMccExportModuleData.rejected, (state, action) => {
                state.exportData.mcc.loading = false;
                state.exportData.mcc.error = action.error.message;
                state.exportData.mcc.data = [];
                state.jobs = handleJobFail(state.jobs, 'mcc');
            })
            // GET ALL MCC EXPORT HISTORY DATA
            .addCase(getAllMccExportHistoryData.fulfilled, (state, action) => {
                state.exportData.mcc.history = action.payload || [];
            })


            // GET ALL COUNTRY CODE BLOCK EXPORT MODULE DATA
            .addCase(getAllCountryCodeBlockExportModuleData.pending, (state) => {
                state.exportData.countryCodeBlock.loading = true;
                state.exportData.countryCodeBlock.error = null;
                state.jobs.push(handleJobPending(state.jobs, 'countryCodeBlock'));
            })
            .addCase(getAllCountryCodeBlockExportModuleData.fulfilled, (state, action) => {
                state.exportData.countryCodeBlock.loading = false;
                state.exportData.countryCodeBlock.data = action.payload || [];
                state.jobs = handleJobComplete(state.jobs, 'countryCodeBlock');
            })
            .addCase(getAllCountryCodeBlockExportModuleData.rejected, (state, action) => {
                state.exportData.countryCodeBlock.loading = false;
                state.exportData.countryCodeBlock.error = action.error.message;
                state.exportData.countryCodeBlock.data = [];
                state.jobs = handleJobFail(state.jobs, 'countryCodeBlock');
            })
            // GET ALL COUNTRY CODE BLOCK EXPORT HISTORY DATA
            .addCase(getAllCountryCodeBlockExportHistoryData.fulfilled, (state, action) => {
                state.exportData.countryCodeBlock.history = action.payload || [];
            })


            // GET ALL Authentication EXPORT MODULE DATA
            .addCase(getAllAuthenticationExportModuleData.pending, (state) => {
                state.exportData.authentication.loading = true;
                state.exportData.authentication.error = null;
                state.jobs.push(handleJobPending(state.jobs, 'authentication'));
            })
            .addCase(getAllAuthenticationExportModuleData.fulfilled, (state, action) => {
                state.exportData.authentication.loading = false;
                state.exportData.authentication.data = action.payload || [];
                state.jobs = handleJobComplete(state.jobs, 'authentication');
            })
            .addCase(getAllAuthenticationExportModuleData.rejected, (state, action) => {
                state.exportData.authentication.loading = false;
                state.exportData.authentication.error = action.error.message;
                state.exportData.authentication.data = [];
                state.jobs = handleJobFail(state.jobs, 'authentication');
            })
            // GET ALL Authentication EXPORT HISTORY DATA
            .addCase(getAllAuthenticationExportHistoryData.fulfilled, (state, action) => {
                state.exportData.authentication.history = action.payload || [];
            })

            // GET SPECIFIC AUTHENTICATION Report DOWNLOAD
            .addCase(getSpecificAuthenticationHistoryData.pending, (state, action) => {
                const { id } = action.meta.arg;
                state.specificLoading = id;
            })
            .addCase(getSpecificAuthenticationHistoryData.fulfilled, (state, action) => {
                state.specificLoading = null;
            })
            .addCase(getSpecificAuthenticationHistoryData.rejected, (state, action) => {
                state.specificLoading = null;
            })

             // GET SPECIFIC Role Report DOWNLOAD
            .addCase(getSpecificRoleHistoryData.pending, (state, action) => {
                const { id } = action.meta.arg;
                state.specificLoading = id;
            })
            .addCase(getSpecificRoleHistoryData.fulfilled, (state, action) => {
                state.specificLoading = null;
            })
            .addCase(getSpecificRoleHistoryData.rejected, (state, action) => {
                state.specificLoading = null;
            })
            // GET SPECIFIC User Report DOWNLOAD
            .addCase(getSpecificUserHistoryData.pending, (state, action) => {
                const { id } = action.meta.arg;
                state.specificLoading = id;
            })
            .addCase(getSpecificUserHistoryData.fulfilled, (state, action) => {
                state.specificLoading = null;
            })
            .addCase(getSpecificUserHistoryData.rejected, (state, action) => {
                state.specificLoading = null;
            })

             // GET SPECIFIC Participant Report DOWNLOAD
            .addCase(getSpecificParticipantHistoryData.pending, (state, action) => {
                const { id } = action.meta.arg;
                state.specificLoading = id;
            })
            .addCase(getSpecificParticipantHistoryData.fulfilled, (state, action) => {
                state.specificLoading = null;
            })
            .addCase(getSpecificParticipantHistoryData.rejected, (state, action) => {
                state.specificLoading = null;
            })

             // GET SPECIFIC Rule Report DOWNLOAD
            .addCase(getSpecificRuleEngineHistoryData.pending, (state, action) => {
                const { id } = action.meta.arg;
                state.specificLoading = id;
            })
            .addCase(getSpecificRuleEngineHistoryData.fulfilled, (state, action) => {
                state.specificLoading = null;
            })
            .addCase(getSpecificRuleEngineHistoryData.rejected, (state, action) => {
                state.specificLoading = null;
            })

             // GET SPECIFIC MCC Report DOWNLOAD
            .addCase(getSpecificMccHistoryData.pending, (state, action) => {
                const { id } = action.meta.arg;
                state.specificLoading = id;
            })
            .addCase(getSpecificMccHistoryData.fulfilled, (state, action) => {
                state.specificLoading = null;
            })
            .addCase(getSpecificMccHistoryData.rejected, (state, action) => {
                state.specificLoading = null;
            })

             // GET SPECIFIC COUNTRY CODE Report DOWNLOAD
            .addCase(getSpecificCountryCodeBlockHistoryData.pending, (state, action) => {
                const { id } = action.meta.arg;
                state.specificLoading = id;
            })
            .addCase(getSpecificCountryCodeBlockHistoryData.fulfilled, (state, action) => {
                state.specificLoading = null;
            })
            .addCase(getSpecificCountryCodeBlockHistoryData.rejected, (state, action) => {
                state.specificLoading = null;
            });
    },
});

export default reportCatalogSlice.reducer;